import type { UnitFormatter } from '@/features/units';
import type { Station } from '../model/types';
import { MARKER_RADIUS } from './markerImage';
import type { StationProperties } from './stationsStyle';

/** Open Sans figures and "+" are tabular: each is this wide, em. */
const FIGURE_EM = 0.58;
/** Text sizes of the wind and of the gust beside it, px. */
export const WIND_TEXT_SIZE = 13;
export const GUST_TEXT_SIZE = 9;
const GAP = 1;
/** How far the gust is raised, like a superscript, px. */
const GUST_RAISE = 3.5;
/** Widest label that stays inside the circle, px; wider ones are scaled down. */
const MAX_WIDTH = 2 * MARKER_RADIUS - 4;

export interface WindLabel {
  /** Mean wind, "12". */
  wind: string;
  /** How much stronger the gusts are, "+4", or "" when no stronger. */
  gust: string;
  /** Text size factor, below 1 for labels too wide for the circle. */
  scale: number;
  /** Text offsets, em of each text's size, so the two read as one centred label. */
  windOffset: [number, number];
  gustOffset: [number, number];
}

/** Wind label: mean speed, and the gust as a small raised difference ("12 ⁺⁴"), in the display unit. */
export function windLabel(s: Station, fmt: UnitFormatter): WindLabel {
  const speed = fmt.speedValue(s.windSpeed);
  const extra = s.windGust === null ? 0 : fmt.speedValue(s.windGust) - speed;
  const wind = String(speed);
  const gust = extra > 0 ? `+${extra}` : '';
  const windWidth = wind.length * FIGURE_EM * WIND_TEXT_SIZE;
  const gustWidth = gust.length * FIGURE_EM * GUST_TEXT_SIZE;
  const total = windWidth + (gust ? GAP + gustWidth : 0);
  return {
    wind,
    gust,
    scale: Math.min(1, MAX_WIDTH / total),
    windOffset: [(-total / 2 + windWidth / 2) / WIND_TEXT_SIZE, 0],
    gustOffset: [(total / 2 - gustWidth / 2) / GUST_TEXT_SIZE, -GUST_RAISE / GUST_TEXT_SIZE],
  };
}

/** Stations in drawing order (first = top) as GeoJSON points. */
export function toFeatures(stations: readonly Station[], fmt: UnitFormatter): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: stations.map((s, i): GeoJSON.Feature<GeoJSON.Point, StationProperties> => {
      // Calm air has no direction worth showing; arrows point downwind.
      const directed = s.windDirection !== null && s.windSpeed >= 0.5;
      return {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [s.lng, s.lat] },
        properties: {
          id: s.id,
          speed: s.windSpeed,
          ...windLabel(s, fmt),
          rotation: directed ? (s.windDirection! + 180) % 360 : -1,
          rank: stations.length - i,
        },
      };
    }),
  };
}
